import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api } from '@/api/client';
import { formatNumber } from '@/utils/helpers';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { exportToCSV } from '@/utils/exportUtils';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { TrendingUp, Package, DollarSign, Download, Calendar, Award } from 'lucide-react';

const COLORS = ['#6d28d9', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export const Reports = () => {
  const currency = useCurrency();
  const [salesData, setSalesData] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [inventoryData, setInventoryData] = useState(null);
  const [staffSales, setStaffSales] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [sales, top, inv, staff] = await Promise.all([
        api.getSalesReport({ start_date: dateFrom, end_date: dateTo }),
        api.getTopProducts(10),
        api.getInventoryReport(),
        api.getStaffSalesReport({ start_date: dateFrom, end_date: dateTo })
      ]);
      setSalesData(sales.data);
      setTopProducts(top.data.products);
      setInventoryData(inv.data);
      setStaffSales(staff.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const setPreset = (preset) => {
    const today = new Date();
    const to = today.toISOString().split('T')[0];
    let from;
    if (preset === 'today') from = to;
    else if (preset === 'week') {
      const d = new Date(); d.setDate(d.getDate() - 7);
      from = d.toISOString().split('T')[0];
    } else if (preset === 'month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    } else if (preset === 'year') {
      from = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
    } else {
      return;
    }
    setDateFrom(from);
    setDateTo(to);
  };

  const handleExportStaff = () => {
    if (!staffSales?.rows) return;
    exportToCSV(
      staffSales.rows,
      'staff_sales_report',
      [
        { key: 'user_name', label: 'Salesperson' },
        { key: 'invoice_count', label: 'Invoices' },
        { key: 'total_sales', label: 'Total Sales' },
        { key: 'total_cost', label: 'Total Cost' },
        { key: 'profit', label: 'Profit' },
        { key: 'average_invoice', label: 'Avg Invoice' },
        { key: 'last_sale_date', label: 'Last Sale' }
      ]
    );
  };

  const handleExportSales = () => {
    if (!salesData?.by_date) return;
    exportToCSV(
      salesData.by_date,
      'sales_report',
      [
        { key: 'date', label: 'Date' },
        { key: 'count', label: 'Orders' },
        { key: 'revenue', label: 'Revenue' }
      ]
    );
  };

  const handleExportProducts = () => {
    exportToCSV(
      topProducts,
      'top_products',
      [
        { key: 'product_name', label: 'Product' },
        { key: 'qty_sold', label: 'Qty Sold' },
        { key: 'revenue', label: 'Revenue' }
      ]
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Reports & Analytics</h1>
      </div>

      {/* Date Range Filter */}
      <Card>
        <div className="flex items-end gap-4 flex-wrap">
          <Calendar size={20} className="text-gray-400 mb-2" />
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
          </div>
          <Button onClick={loadReports} disabled={loading} data-testid="apply-date-range">
            {loading ? 'Loading...' : 'Apply'}
          </Button>
          <div className="flex gap-1 ml-2">
            <Button size="sm" variant="outline" onClick={() => setPreset('today')} data-testid="preset-today">Today</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('week')} data-testid="preset-week">7d</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('month')} data-testid="preset-month">Month</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset('year')} data-testid="preset-year">Year</Button>
          </div>
        </div>
      </Card>

      {/* Sales Summary KPIs */}
      {salesData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(salesData.total_revenue, currency)}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                <DollarSign className="text-green-600 dark:text-green-400" size={28} />
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Orders Completed</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{salesData.total_count}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Package className="text-blue-600 dark:text-blue-400" size={28} />
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Average Sale</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(salesData.average_sale, currency)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <TrendingUp className="text-purple-600 dark:text-purple-400" size={28} />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Sales Over Time Chart */}
      <Card title="Sales Over Time">
        <div className="flex justify-end mb-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportSales}
            disabled={!salesData?.by_date?.length}
            data-testid="export-sales-csv"
          >
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        </div>
        {salesData?.by_date?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesData.by_date}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="date" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', color: '#fff' }} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#06b6d4" strokeWidth={2} name={`Revenue (${currency})`} />
              <Line type="monotone" dataKey="count" stroke="#a855f7" strokeWidth={2} name="Orders" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
            No completed sales in selected date range. Try expanding the date range.
          </div>
        )}
      </Card>

      {/* Payment Methods & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Revenue by Payment Method">
          {salesData?.by_payment_method?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={salesData.by_payment_method}
                  dataKey="amount"
                  nameKey="method"
                  cx="50%"
                  cy="50%"
                  outerRadius={75}
                  label={({ method, percent }) => `${method} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {salesData.by_payment_method.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => formatCurrency(v, currency)}
                  contentStyle={{ background: '#1f2937', border: '1px solid #374151', color: '#fff' }}
                />
                <Legend verticalAlign="bottom" height={28} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              No payment data in selected period
            </div>
          )}
        </Card>

        {/* Top Products */}
        <Card title={<div className="flex items-center gap-2"><Award size={18} /> Top 10 Products</div>}>
          <div className="flex justify-end mb-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExportProducts}
              disabled={topProducts.length === 0}
              data-testid="export-products-csv"
            >
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topProducts.slice(0, 5)} layout="vertical" margin={{ left: 10, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" stroke="#888" tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis type="category" dataKey="product_name" stroke="#888" width={130} tick={{ fontSize: 11 }} tickFormatter={(v) => v && v.length > 18 ? v.slice(0, 18) + '…' : v} />
                <Tooltip formatter={(v) => formatCurrency(v, currency)} contentStyle={{ background: '#1f2937', border: '1px solid #374151', color: '#fff' }} />
                <Bar dataKey="revenue" fill="#6d28d9" name={`Revenue (${currency})`} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              No completed sales yet
            </div>
          )}
        </Card>
      </div>

      {/* Inventory Report */}
      {inventoryData && (
        <Card title="Inventory Summary">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400">Total Products</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{inventoryData.total_products}</p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400">Units in Stock</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{inventoryData.total_units_in_stock}</p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400">Total Inventory Value</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatCurrency(inventoryData.total_inventory_value, currency)}
              </p>
            </div>
          </div>

          {inventoryData.by_category?.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={inventoryData.by_category} margin={{ top: 10, right: 12, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="category" stroke="#888" interval={0} angle={-30} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                <YAxis stroke="#888" tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip
                  formatter={(v, name) => name && String(name).startsWith('Value') ? formatCurrency(v, currency) : v}
                  contentStyle={{ background: '#1f2937', border: '1px solid #374151', color: '#fff' }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="units" fill="#06b6d4" name="Units in Stock" radius={[4, 4, 0, 0]} />
                <Bar dataKey="value" fill="#10b981" name={`Value (${currency})`} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {/* Top Products Table */}
      {topProducts.length > 0 && (
        <Card title="All Top Selling Products">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Units Sold</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {topProducts.map((p, i) => (
                  <tr key={p.product_id}>
                    <td className="px-6 py-4 text-sm font-bold">{i + 1}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{p.product_name}</td>
                    <td className="px-6 py-4 text-sm">{p.qty_sold}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(p.revenue, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Staff Performance */}
      {staffSales && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Award className="text-amber-500" size={20} />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Staff Performance</h3>
              <span className="text-xs text-gray-500">({staffSales.from || 'all'} → {staffSales.to || 'now'})</span>
            </div>
            <Button size="sm" variant="outline" onClick={handleExportStaff} disabled={!staffSales.rows?.length} data-testid="export-staff-csv">
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(staffSales.total_revenue, currency)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Total Profit</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(staffSales.total_profit, currency)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(staffSales.total_invoices)}</p>
            </div>
          </div>
          {staffSales.rows?.length ? (
            <div className="overflow-x-auto" data-testid="staff-sales-table">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Salesperson</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Invoice</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Sale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {staffSales.rows.map((r, i) => (
                    <tr key={r.user_id || i} data-testid={`staff-row-${i}`}>
                      <td className="px-4 py-2 text-sm font-bold">{i + 1}</td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">{r.user_name}</td>
                      <td className="px-4 py-2 text-sm text-right">{r.invoice_count}</td>
                      <td className="px-4 py-2 text-sm text-right font-semibold">{formatCurrency(r.total_sales, currency)}</td>
                      <td className="px-4 py-2 text-sm text-right text-emerald-600 dark:text-emerald-400">{formatCurrency(r.profit, currency)}</td>
                      <td className="px-4 py-2 text-sm text-right">{formatCurrency(r.average_invoice, currency)}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{r.last_sale_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-gray-400">No staff sales in this period.</div>
          )}
        </Card>
      )}
    </div>
  );
};
